/* eslint-disable max-len */
import { CircularProgress, makeStyles } from '@material-ui/core';
import { MetricInfo, MetricValueType } from '@moderepo/mode-apis';
import { TimeSeriesType } from '@moderepo/mode-app-components';
import clsx from 'clsx';
import { CSSProperties, useCallback, useEffect, useMemo, useState } from 'react';
import { EntitySingleMetricViewDefault, SingleMetricData, fetchSingleMetricData, EntitySingleMetricViewDefaultProps } from '.';
import { EntityUIComponentCollection, EntityUIComponentCustomActionsSettings, getMetricsDefinitionMetricsId,
    isEntityUIComponentCustomActionsSettings, useSelectTranslatedEntityClasses } from '..';
import { RouteHelper } from '../..';
import { useRitzTranslation } from '../../../utils/customHooks';
import { useSelectEntity, useSelectProject } from '../../../utils/dataStateHooks';
import { SettingsErrorLogger } from '../../../utils/logger';
import { RitzComponentsCollection } from '../../../utils/ritzComponentsCollection';
import { isObjectLiteral, isValidSettingBooleanValue, isValidSettingStringValue } from '../../../utils/utils';
import { EntityUIComponentProps } from '../EntityDetailsLayouts';


const useStyle = makeStyles(() => {
    return {
        root: {
            position      : 'relative',
            overflow      : 'hidden',
            display       : 'flex',
            alignItems    : 'center',
            justifyContent: 'center',
            height        : '100%',
        },
    };
}, { name: 'EntityLatestSingleMetricValueUIComponent', index: 1 });



export enum EntitySingleMetricValueViewLayout {
    VERTICAL = 'vertical',
    HORIZONTAL = 'horizontal'
}



/**
 * This is the interface for the EntityLatestSingleMetricValueUIComponent's CustomSettings.
 */
export interface EntityLatestSingleMetricValueUIComponentCustomSettings extends EntityUIComponentCustomActionsSettings {

    // The metric we want to display. We will need both metricsDefinitionId and metricName to identify the metric.
    readonly metricsDefinitionId: string;
    readonly metricName: string;

    // metricsDefinitionId and metricName is not enough to identify the metric because it is possible for a numeric metric and tag metric
    // to have the same name in 1 metrics definition. Therefore, we need this is additional info to find the metric. If this value is 'number', will
    // will look for the metricInfo in the metricsDefinition.numericMetrics array. If this value is 'string', we will look for the metric info
    // in the metricsDefinition.tagMetrics array.
    // This is OPTIONAL because it is only needed for metrics definition that have numeric metrics and tag metrics with the same name. Also some
    // metricsDefinition don't have numericMetrics/tagMetrics.
    readonly metricValueType?: MetricValueType | undefined;

    // The view layout to use. The default value is 'horizontal' so this option is not required unless we want to use 'vertical' layout
    readonly layout?: EntitySingleMetricValueViewLayout | undefined;

    // Optional label to show in front of the value. The DEFAULT will be the metric's displayName if label is not provided.
    readonly label?: string | undefined;

    // Whether or not to also show the metric value's timestamp. Default is TRUE
    readonly showDateTime?: boolean | undefined;

    // Whether or not to also show the metric's unit if the metric has a unit. Default is TRUE
    readonly showUnit?: boolean | undefined;

    // The custom CSS style for the outer container element
    readonly containerStyle?: CSSProperties | undefined;

    // The custom CSS style for the display's label
    readonly labelStyle?: CSSProperties | undefined;

    // The custom CSS style for the display's value
    readonly valueStyle?: CSSProperties | undefined;

    // The custom CSS style for the display's unit
    readonly unitStyle?: CSSProperties | undefined;

    // The custom CSS style for the display's timestamp
    readonly dateTimeStyle?: CSSProperties | undefined;

    // The custom CSS style for the display's action
    readonly actionStyle?: CSSProperties | undefined;
}


/**
 * Type guard function to validate the obj to make sure it is a type of EntityLatestSingleMetricValueUIComponentCustomSettings
 */
export const isEntityLatestSingleMetricValueUIComponentCustomSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is EntityLatestSingleMetricValueUIComponentCustomSettings => {
    const settings = obj as EntityLatestSingleMetricValueUIComponentCustomSettings;
    const layouts = Object.values(EntitySingleMetricValueViewLayout);
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be undefined or an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    // metricsDefinitionId and metricName are required
    if (!isValidSettingStringValue(settings.metricsDefinitionId, true)) {
        return errorLogger?.logError({
            message: '"metricsDefinitionId" is required and must be a non-empty string.',
            value  : settings.metricsDefinitionId,
            path   : [...configPath, 'metricsDefinitionId'],
        }) ?? false;
    }
    if (!isValidSettingStringValue(settings.metricName, true)) {
        return errorLogger?.logError({
            message: '"metricName" is required and must be a non-empty string.',
            value  : settings.metricName,
            path   : [...configPath, 'metricName'],
        }) ?? false;
    }
    if (!isValidSettingStringValue(settings.metricValueType, false, false, Object.values(MetricValueType))) {
        return errorLogger?.logError({
            message: `"metricValueType" must be undefined or one of the following values [${
                Object.values(MetricValueType).map((type) => {
                    return `"${type}"`;
                }).join(', ')
            }]`,
            value: settings.metricValueType,
            path : [...configPath, 'metricValueType'],
        }) ?? false;
    }

    // layout is optional but if it is provided, it must be one of the supported view layout
    if (!isValidSettingStringValue(settings.layout, false, false, layouts)) {
        return errorLogger?.logError({
            message: `"layout" must be undefined or one of the following [${
                layouts.map((layout) => {
                    return `"${layout}"`;
                }).join(', ')}].`,
            value: settings.layout,
            path : [...configPath, 'layout'],
        }) ?? false;
    }

    // label is optional but if they are provided, they must be strings
    if (!isValidSettingStringValue(settings.label)) {
        return errorLogger?.logError({
            message: '"label" must be undefined or a non-empty string.',
            value  : settings.label,
            path   : [...configPath, 'label'],
        }) ?? false;
    }

    // showDateTime and showUnit are optional but if they are provided, they must be strings
    if (!isValidSettingBooleanValue(settings.showDateTime)) {
        return errorLogger?.logError({
            message: '"showDateTime" must be undefined or a boolean.',
            value  : settings.showDateTime,
            path   : [...configPath, 'showDateTime'],
        }) ?? false;
    }
    if (!isValidSettingBooleanValue(settings.showUnit)) {
        return errorLogger?.logError({
            message: '"showUnit" must be undefined or a boolean.',
            value  : settings.showUnit,
            path   : [...configPath, 'showUnit'],
        }) ?? false;
    }

    // containerStyle, iconStyle, labelStyle, valueStyle, unitStyle, dateTimeStyle are optional but if they are provided, they must be objects
    if (settings.containerStyle !== undefined && !isObjectLiteral(settings.containerStyle)) {
        return errorLogger?.logError({
            message: '"containerStyle" must be undefined or a CSSProperties object.',
            value  : settings.containerStyle,
            path   : [...configPath, 'containerStyle'],
        }) ?? false;
    }
    if (settings.labelStyle !== undefined && !isObjectLiteral(settings.labelStyle)) {
        return errorLogger?.logError({
            message: '"labelStyle" must be undefined or a CSSProperties object.',
            value  : settings.labelStyle,
            path   : [...configPath, 'labelStyle'],
        }) ?? false;
    }
    if (settings.valueStyle !== undefined && !isObjectLiteral(settings.valueStyle)) {
        return errorLogger?.logError({
            message: '"valueStyle" must be undefined or a CSSProperties object.',
            value  : settings.valueStyle,
            path   : [...configPath, 'valueStyle'],
        }) ?? false;
    }
    if (settings.unitStyle !== undefined && !isObjectLiteral(settings.unitStyle)) {
        return errorLogger?.logError({
            message: '"unitStyle" must be undefined or a CSSProperties object.',
            value  : settings.unitStyle,
            path   : [...configPath, 'unitStyle'],
        }) ?? false;
    }
    if (settings.dateTimeStyle !== undefined && !isObjectLiteral(settings.dateTimeStyle)) {
        return errorLogger?.logError({
            message: '"dateTimeStyle" must be undefined or a CSSProperties object.',
            value  : settings.dateTimeStyle,
            path   : [...configPath, 'dateTimeStyle'],
        }) ?? false;
    }
    if (settings.actionStyle !== undefined && !isObjectLiteral(settings.actionStyle)) {
        return errorLogger?.logError({
            message: '"actionStyle" must be undefined or a CSSProperties object.',
            value  : settings.actionStyle,
            path   : [...configPath, 'actionStyle'],
        }) ?? false;
    }

    // Because settings is also an EntityUIComponentCustomActionsSettings, we can call this type guard function to validate the props defined
    // for "EntityUIComponentCustomActionsSettings"
    if (!isEntityUIComponentCustomActionsSettings(settings, errorLogger, [...configPath])) {
        return false;
    }

    return true;
};



export interface EntityLatestSingleMetricValueUIComponentProps extends EntityUIComponentProps {
    readonly customSettings?: EntityLatestSingleMetricValueUIComponentCustomSettings | undefined;
}


/**
 * This EntityUIComponent is used for showing an entity's 1 single metric value
 */
export const EntityLatestSingleMetricValueUIComponent: React.FC<EntityLatestSingleMetricValueUIComponentProps> = (
    props: EntityLatestSingleMetricValueUIComponentProps,
) => {

    const project = useSelectProject(props.projectId);
    const { trans } = useRitzTranslation(project);
    const entityClasses = useSelectTranslatedEntityClasses(props.projectId, trans);
    const selectedEntity = useSelectEntity(props.projectId, props.entityId, false);

    /**
     * Find the entity class tied to the selectedEntity
     */
    const selectedEntityClass = useMemo(() => {
        if (selectedEntity && entityClasses) {
            return entityClasses.find((entityClass) => {
                return entityClass.entityClass === selectedEntity.entityClass;
            });
        }
        return undefined;
    }, [entityClasses, selectedEntity]);


    /**
     * Find the metricsDefinition from the entityClass based on the customSettings.metricsDefinition
     */
    const selectedMetricsDefinition = useMemo(() => {
        if (selectedEntityClass) {
            return selectedEntityClass.metricsDefinitions?.find((metricsDef) => {
                return metricsDef.metricsDefinitionId === props.customSettings?.metricsDefinitionId;
            });
        }
        return undefined;
    }, [props.customSettings?.metricsDefinitionId, selectedEntityClass]);


    /**
     * Find the metric info from the metrics definition based on the customSettings.metricName. Each metrics definitions has an array of
     * numeric metrics and an array of tag metrics. With the "metricName", we can loop through these numeric metrics and tag metrics array to
     * find the metric info has the same name as metricName. HOWEVER, it is possible that we find both numeric metrics and tag metrics has
     * a metric info that has the same name. This is why we have an OPTIONAL attribute "customSettings.metricValueType" that the admin can
     * provide to tell this component specifically which array to use when looking up metric info. If "metricValueType" === "number", we will
     * look in numericMetrics. If "metricValueType" === "string", we will look in tagMetrics. If this is not provided, then we will check both
     * and return whichever we find first.
     */
    const { selectedMetricInfo, metricValueType } = useMemo((): {
        readonly selectedMetricInfo: MetricInfo | undefined;
        readonly metricValueType: MetricValueType | undefined;
    } => {
        if (selectedMetricsDefinition && props.customSettings) {
            const customSettings = props.customSettings;

            // Find the numericMetric and tagMetric that matches the metricName
            const numericMetric = selectedMetricsDefinition?.numericMetrics?.find((metric) => {
                return metric.name === customSettings.metricName;
            });
            const tagMetric = selectedMetricsDefinition?.tagMetrics?.find((metric) => {
                return metric.name === customSettings.metricName;
            });


            // If metricValueType is specified in the customSettings then return the metricInfo based on the metricValueType
            if (props.customSettings.metricValueType === MetricValueType.NUMBER) {
                return {
                    selectedMetricInfo: numericMetric ? {
                        ...numericMetric,
                        // Use numericMetrics' evaluator's displayName is possible
                        displayName: numericMetric?.evaluator?.displayName ?? numericMetric?.name,
                    } : undefined,
                    metricValueType: customSettings.metricValueType,
                };
            }

            if (customSettings.metricValueType === MetricValueType.STRING) {
                return {
                    selectedMetricInfo: tagMetric,
                    metricValueType   : customSettings.metricValueType,
                };
            }

            // If metricValueType is not provided then we just return whichever metrics definition that we can find. If there is only 1 metric that
            // have the same name then everything is good. If there is a numericMetric and tagMetric with the same name then we return the
            // numericMetric. If this is not what the user wanted then they should provide a "metricValueType".
            // Display a warning in the console if this happen.
            if (numericMetric && tagMetric) {
                // eslint-disable-next-line no-console
                console.warn(
                    `Metric with the name '${customSettings.metricName}' exists in both numericMetrics and tagMetrics. Please specify the
                    'metricValueType' in the settings.`,
                );
            }

            // We found a NUMERIC metricInfo that matches the metricName, return the numericMetric object and the metricValueType will be 'number'
            if (numericMetric) {
                return {
                    selectedMetricInfo: {
                        ...numericMetric,
                        displayName: numericMetric.evaluator?.displayName ?? numericMetric.name, // Use numericMetrics' evaluator's displayName is possible
                    },
                    metricValueType: MetricValueType.NUMBER,      // return "metricValueType" = "number" because we found the metric info in numericMetrics array.
                };
            }

            // We found a TAG metricInfo that matches the metricName, return the tagMetric object and the metricValueType will be 'string'
            if (tagMetric) {
                return {
                    selectedMetricInfo: tagMetric,
                    metricValueType   : MetricValueType.STRING,   // return "metricValueType" = "string" because we found the metric info in tagMetrics array.
                };
            }
        }

        return {
            selectedMetricInfo: undefined, metricValueType: undefined,
        };
    }, [props.customSettings, selectedMetricsDefinition]);


    const classes = useStyle();


    // The state that keep track of the metric data to be displayed. This object contains the metric name, value, unit, and date
    const [singleMetricData, setSingleMetricData] = useState<SingleMetricData | undefined>();

    /**
     * Call 'fetchSingleMetricData' to fetch 1 metric value and store the result to the "singleMetricData" state
     */
    useEffect(() => {
        if (props.customSettings && selectedEntity && selectedMetricsDefinition) {
            fetchSingleMetricData(props.customSettings, metricValueType, selectedEntity, selectedMetricsDefinition, selectedMetricInfo, trans).then((result) => {
                setSingleMetricData(result);
            });
        }
    }, [metricValueType, props.customSettings, selectedEntity, selectedMetricInfo, selectedMetricsDefinition, trans]);



    /**
     * If the "clickable" is set to true then take the user to the historical data when the component is clicked
     */
    const onClickHandler = useCallback((event?: React.MouseEvent<HTMLElement, MouseEvent> | undefined) => {
        // If the parent container has onClick handler then let the parent container handle the event
        if (props.onClick) {
            props.onClick(event);
        } else if (selectedEntity && selectedMetricsDefinition) {
            if (event) {
                event.stopPropagation();
                event.preventDefault();
            }

            // Get the collectionId for the given metricsDefinitionId
            const collectionId = getMetricsDefinitionMetricsId(selectedEntity, selectedMetricsDefinition.metricsDefinitionId);

            RouteHelper.getInstance().gotoHomeHistoricalDataPage({
                projectId        : props.projectId,
                homeId           : selectedEntity.homeId,
                tsdbSmartModuleId: selectedMetricsDefinition.dataStoreConfiguration.moduleInstanceId,
                dataId           : collectionId,
                timeSeriesType   : TimeSeriesType.COLLECTION,
                metricsDefinition: {
                    entityClass        : selectedEntity.entityClass,
                    metricsDefinitionId: selectedMetricsDefinition.metricsDefinitionId,
                },
            });
        }
    }, [props, selectedEntity, selectedMetricsDefinition]);



    /**
     * If "customSettings.action" is provided, get the icon and label from the customSettings.action and for onClick callback,
     * use the onClickHandler. Create an Action object and pass it to the EntitySingleMetricViewDefault components
     */
    const action = useMemo((): EntitySingleMetricViewDefaultProps['action'] => {
        if (props.customSettings?.actions && props.customSettings.actions.length > 0) {
            return {
                ...props.customSettings.actions[0],
                onClick: onClickHandler,
            };
        }
        return undefined;
    }, [onClickHandler, props.customSettings?.actions]);


    return (
        <>
            {selectedEntity && (
                <EntitySingleMetricViewDefault
                    className={clsx(props.className, classes.root)}
                    style={props.style}
                    projectId={props.projectId}
                    customSettings={props.customSettings}
                    singleMetricData={singleMetricData}
                    action={action}
                />
            )}

            {!selectedEntity && (
                <CircularProgress />
            )}
        </>
    );
};


// Register this component with the RitzComponentsCollection so that it can be used with the Ritz UI customization system
RitzComponentsCollection.getInstance().registerComponent(
    'EntityLatestSingleMetricValueUIComponent', EntityLatestSingleMetricValueUIComponent,
);



// Register the component with the EntityUIComponentCollection so that the component can be used by the Entity Layout System
EntityUIComponentCollection.getInstance().registerComponent(
    'Entity_Latest_Single_Metric_Value_Comp',
    {
        componentFunc          : EntityLatestSingleMetricValueUIComponent,
        customSettingsValidator: isEntityLatestSingleMetricValueUIComponentCustomSettings,
    },
);
